import { useState, useCallback } from "react";
import { Map, Marker, APIProvider } from "@vis.gl/react-google-maps";

interface LocationPickerProps {
  onLocationSelect: (location: {
    lat: number;
    long: number;
    address: string;
  }) => void;
  initialLocation?: {
    lat: number;
    long: number;
    address: string;
  };
}

export default function LocationPicker({
  onLocationSelect,
  initialLocation,
}: LocationPickerProps) {
  const [latitude, setLatitude] = useState(initialLocation?.lat || 25.6866);
  const [longitude, setLongitude] = useState(initialLocation?.long || -100.3161);
  const [address, setAddress] = useState(initialLocation?.address || "");
  const [searchInput, setSearchInput] = useState("");

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

  const handleMapClick = useCallback(
    (event: any) => {
      if (!event.detail.latLng) return;
      const lat = event.detail.latLng.lat;
      const lng = event.detail.latLng.lng;

      setLatitude(lat);
      setLongitude(lng);
      const newAddress = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setAddress(newAddress);

      onLocationSelect({
        lat,
        long: lng,
        address: newAddress,
      });
    },
    [onLocationSelect]
  );

  const handleMarkerDragEnd = useCallback(
    (e: any) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setLatitude(lat);
      setLongitude(lng);
      const newAddress = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setAddress(newAddress);

      onLocationSelect({
        lat,
        long: lng,
        address: newAddress,
      });
    },
    [onLocationSelect]
  );

  const handleSearchAddress = async () => {
    if (!searchInput.trim() || !apiKey) return;

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          searchInput
        )}&key=${apiKey}`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const lat = result.geometry.location.lat;
        const lng = result.geometry.location.lng;
        const placeName = result.formatted_address;

        setLatitude(lat);
        setLongitude(lng);
        setAddress(placeName);
        setSearchInput(placeName);

        onLocationSelect({
          lat,
          long: lng,
          address: placeName,
        });
      }
    } catch (error) {
      console.error("Error searching address:", error);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-3">
        <label className="block text-xs text-content-muted mb-1.5">
          Buscar dirección
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") handleSearchAddress();
            }}
            placeholder="Ingresa una dirección..."
            className="flex-1 px-3 py-2.5 rounded-lg border border-line bg-surface text-content text-sm placeholder-content-muted focus:outline-none focus:border-primary-500"
          />
          <button
            type="button"
            onClick={handleSearchAddress}
            className="px-3 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors"
          >
            <i className="ri-search-line" />
          </button>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-xs text-content-muted mb-1.5">
          O haz clic/arrastra el marcador en el mapa
        </label>
        <div className="rounded-lg border border-line overflow-hidden bg-surface h-64">
          {apiKey ? (
            <APIProvider apiKey={apiKey}>
              <Map
                defaultZoom={10}
                defaultCenter={{ lat: latitude, lng: longitude }}
                onClick={handleMapClick}
                gestureHandling="greedy"
                mapId="LOCATION_PICKER"
              >
                <Marker
                  position={{ lat: latitude, lng: longitude }}
                  draggable
                  onDragEnd={handleMarkerDragEnd}
                />
              </Map>
            </APIProvider>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-subtle">
              <p className="text-content-muted text-sm">
                API key de Google Maps no configurada
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="bg-subtle rounded-lg p-2">
          <p className="text-xs text-content-muted">Latitud</p>
          <p className="text-content font-mono font-semibold truncate">
            {latitude.toFixed(6)}
          </p>
        </div>
        <div className="bg-subtle rounded-lg p-2">
          <p className="text-xs text-content-muted">Longitud</p>
          <p className="text-content font-mono font-semibold truncate">
            {longitude.toFixed(6)}
          </p>
        </div>
        <div className="bg-subtle rounded-lg p-2">
          <p className="text-xs text-content-muted">Dirección</p>
          <p className="text-content font-semibold truncate text-xs">
            {address || "Selecciona"}
          </p>
        </div>
      </div>
    </div>
  );
}
